import { useState, useEffect, useCallback } from "react";
import { tagManager } from "./TagManager";
import type { Tag } from "./types";

interface UseTagManagerByIdsOptions {
  enabled?: boolean;
}

/**
 * React hook to fetch tags by IDs using TagManager
 * Provides caching and automatic deduplication
 *
 * @param ids - Array of tag IDs to fetch
 * @param options - Hook options
 */
export function useTagManagerByIds(
  ids: number[] | null | undefined,
  options?: UseTagManagerByIdsOptions
) {
  const { enabled = true } = options || {};
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !ids || ids.length === 0) {
      setTags([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    tagManager
      .getTagsByIds(ids)
      .then((fetchedTags) => {
        if (!cancelled) {
          setTags(fetchedTags);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ids?.join(" "), enabled]);

  return {
    tags,
    isLoading,
    error,
    isSuccess: !isLoading && !error && tags.length > 0,
  };
}

/**
 * React hook to hydrate tags with missing names using TagManager
 *
 * @param tags - Array of tags to hydrate
 */
export function useTagManagerHydrate(tags: Tag[] | null | undefined) {
  const [hydratedTags, setHydratedTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create a stable key for the tags array
  const tagsKey = tags?.map((t) => `${t.tag_id}-${t.name || ""}`).join(",") || "";

  useEffect(() => {
    if (!tags || tags.length === 0) {
      setHydratedTags([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    tagManager
      .hydrateTags(tags)
      .then((hydrated) => {
        if (!cancelled) {
          setHydratedTags(hydrated);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tagsKey]);

  return {
    tags: hydratedTags,
    isLoading,
    error,
    isSuccess: !isLoading && !error && hydratedTags.length > 0,
  };
}

/**
 * Hook that provides access to tagManager methods
 * Useful for manual cache management
 */
export function useTagManagerActions() {
  const addToCache = useCallback((tags: Tag[]) => {
    tagManager.addToCache(tags);
  }, []);

  const clearCache = useCallback(() => {
    tagManager.clearCache();
  }, []);

  const removeFromCache = useCallback((ids: number[]) => {
    tagManager.removeFromCache(ids);
  }, []);

  const getCacheSize = useCallback(() => {
    return tagManager.getCacheSize();
  }, []);

  const isCached = useCallback((id: number) => {
    return tagManager.isCached(id);
  }, []);

  const getCached = useCallback((id: number) => {
    return tagManager.getCached(id);
  }, []);

  return {
    addToCache,
    clearCache,
    removeFromCache,
    getCacheSize,
    isCached,
    getCached,
  };
}
